import React, { useState } from "react"
import { Grid, Box, TextField, LinearProgress } from "@mui/material"
import { makeStyles } from "makeStyles"
import { Rating } from "@mui/material"
import axios from "axios"
import Icon from "@mdi/react"
import { mdiCheckDecagram, mdiStar } from "@mdi/js"
import { useSnackbar } from "notistack"

import Button from "components/Buttons/Button"
import TextInput from "components/Forms/TextInput"

import useInputValue from "@secureo/common/hooks/useInputValue"

import { ReviewRatingStatistics, Review } from "@secureo/common/typings/Product"
import { RequestBody } from "pages/api/reviews/addProductReview"

// Recaptcha
import ReCAPTCHA from "react-google-recaptcha"

const useStyles = makeStyles()({
	ratingsAlignment: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		borderBottom: "1px dashed grey",
		"@media only screen and (max-width: 800px)": {
			display: "block",
			justifyContent: "unset",
		},
	},
	flexNameAndVerified: {
		display: "flex",
		alignItems: "center",
		"@media only screen and (max-width: 800px)": {
			display: "block",
			justifyContent: "unset",
		},
	},
})

const ReviewForm = ({ accessToken, productId, masterVariantSku }: ReviewFormProps) => {
	const [isLoading, setIsLoading] = useState(false)
	const [, setIsSuccess] = useState(false)
	const [, setIsError] = useState(false)
	const name = useInputValue("")
	const title = useInputValue("")
	const comment = useInputValue("")
	const [rating, setRating] = useState(4.5)
	const { enqueueSnackbar } = useSnackbar()

	const [recaptchaValue, setRecaptchaValue] = useState(false)

	const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault()

		const body: RequestBody = {
			accessToken,
			productId,
			masterVariantSku,
			name: name.value,
			title: title.value,
			comment: comment.value,
			rating,
		}

		if (recaptchaValue === false) {
			enqueueSnackbar("Bitte bestätigen sie das Captcha.", { variant: "error" })
			return
		}

		console.info(recaptchaValue)

		try {
			setIsLoading(true)
			await axios.post("/api/reviews/addProductReview", body)
			setIsSuccess(true)
			enqueueSnackbar("Vielen Dank für Ihre Bewertung!", {
				variant: "success",
			})
		} catch (err) {
			console.error(err)
			setIsError(true)
			enqueueSnackbar("Etwas ist schief gelaufen, bitte probieren Sie es später nochmal", {
				variant: "error",
			})
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<form onSubmit={onSubmit}>
			<Box>
				<strong>Bewerten Sie dieses Produkt</strong>
			</Box>
			<Box mb={2} mt={1}>
				<Rating
					name="rating"
					value={rating}
					size="large"
					precision={0.5}
					onChange={(_, newValue) => {
						setRating(newValue)
					}}
					disabled={isLoading}
				/>
			</Box>
			<div>
				<strong>Schreiben Sie eine Bewertung</strong>
			</div>

			<TextInput name="name" label="Name" {...name} fullWidth required disabled={isLoading} />
			<TextInput
				name="title"
				label="Titel"
				{...title}
				fullWidth
				required
				disabled={isLoading}
			/>
			<TextField
				name="comment"
				label="Kommentar"
				multiline
				rows={4}
				fullWidth
				{...comment}
				disabled={isLoading}
				variant="filled"
				style={{ marginTop: 13 }}
			/>
			<Box mt={2}>
				{/* TODO: Move to .env file */}
				<ReCAPTCHA
					size="normal"
					sitekey="6LfAEkMfAAAAAAvoMnu_fAcAUvHzYkLj03lO6193"
					onChange={setRecaptchaValue}
				/>
			</Box>

			<Box
				mt={2}
				mb={5}
				style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
			>
				<Box style={{ textAlign: "right" }}>
					<Button type="submit" color="primary" disabled={isLoading}>
						Bewerten
					</Button>
				</Box>
			</Box>
		</form>
	)
}

interface ReviewBarsProps {
	reviewRatingStatistics: ReviewRatingStatistics
}

const RatingBars = ({ reviewRatingStatistics }: ReviewBarsProps) => {
	return (
		<div>
			<ul style={{ listStyle: "none", paddingLeft: 0 }}>
				{[1, 2, 3, 4, 5].reverse().map((rating) => {
					const count = reviewRatingStatistics?.ratingsDistribution?.[rating] ?? 0
					const countHalf =
						reviewRatingStatistics?.ratingsDistribution?.[rating - 0.5] ?? 0

					const totalCount = count + countHalf

					const bar = (totalCount / reviewRatingStatistics.count) * 100

					return (
						<li
							key={rating}
							style={{ display: "flex", alignItems: "center", marginBottom: 5 }}
						>
							<Box mr={1} style={{ display: "flex", alignItems: "center" }}>
								{rating} <Icon path={mdiStar} size={0.7} />
							</Box>
							<LinearProgress
								variant="determinate"
								value={bar}
								style={{ width: "100%", maxWidth: 200 }}
							/>{" "}
							<Box ml={1}>{totalCount}</Box>
						</li>
					)
				})}
			</ul>
		</div>
	)
}

interface ReviewFormProps {
	accessToken: string
	productId: string
	masterVariantSku: string
}

const Ratings = ({
	accessToken,
	reviewRatingStatistics,
	reviews,
	productId,
	masterVariantSku,
}: Props) => {
	const { classes } = useStyles()
	const hasReviews = reviews.length > 0
	const averageRating = Number(reviewRatingStatistics.averageRating.toFixed(2))

	return (
		<Grid container spacing={3}>
			{hasReviews && (
				<>
					<Grid item xs={12} md={6}>
						<RatingBars reviewRatingStatistics={reviewRatingStatistics} />
					</Grid>

					<Grid item xs={12} md={6}>
						<div>Durchschnittliche Bewertung</div>
						<div style={{ display: "flex" }}>
							<Rating name="rating" value={averageRating} precision={0.5} readOnly />
							<div>
								<strong>{averageRating} von 5</strong>
							</div>
						</div>
					</Grid>
				</>
			)}
			<Grid item xs={12}>
				<ReviewForm
					accessToken={accessToken}
					productId={productId}
					masterVariantSku={masterVariantSku}
				/>
			</Grid>

			{reviews.length > 0 ? (
				<Grid item xs={12}>
					{reviews.map((review) => {
						const {
							id,
							authorName,
							title,
							text,
							rating,
							createdAt,
							isVerifiedPurchaser,
						} = review

						const date = new Date(createdAt)

						const formattedDate = `${date.getDay()}.${
							date.getMonth() + 1
						}.${date.getFullYear()}`

						return (
							<Box mb={5} key={id}>
								<div className={classes.ratingsAlignment}>
									<div className={classes.flexNameAndVerified}>
										<Box mr={1}>
											{title && <strong>{title} - </strong>}
											<span style={{ color: "grey" }}>
												{authorName} - {formattedDate}
											</span>
										</Box>
										{isVerifiedPurchaser && (
											<Box style={{ display: "flex", alignItems: "center" }}>
												<Icon
													path={mdiCheckDecagram}
													size={0.9}
													color="#4691CE"
													style={{ marginRight: 5 }}
												/>
												verifizierter Kauf
											</Box>
										)}
									</div>
									<div>
										<Rating
											name="rating"
											value={rating}
											precision={0.5}
											readOnly
										/>
									</div>
								</div>
								<Box my={1}>{text}</Box>
							</Box>
						)
					})}
				</Grid>
			) : (
				<Grid item xs={12}>
					<Box my={2}>
						<strong>Für dieses Produkt sind noch keine Bewertungen vorhanden.</strong>
					</Box>
				</Grid>
			)}
		</Grid>
	)
}

interface Props {
	accessToken: string
	productId: string
	masterVariantSku: string
	reviewRatingStatistics: ReviewRatingStatistics
	reviews: Review[]
}

export default Ratings
